/**
 * Navbar - Main navigation component (refactored)
 * 
 * Sub-components extracted to /navbar folder:
 * - NavbarCategories.jsx - HoverDropdown, MobileCategoryItem, MobileCategoriesPanel
 * - NavbarUserMenu.jsx - UserHoverMenu
 * - NavbarNotifications.jsx - NotificationHoverMenu
 * - NavbarMessages.jsx - MessagesHoverMenu
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { messagesAPI, userAPI, categoriesAPI, listingsAPI, ordersAPI, watchlistAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { formatDate, getInitials } from '@/lib/utils';
import {
    Search, Plus, Menu, X, Clock, Package,
    Gavel, Heart, Settings, Shield, LogOut,
    ShoppingBag, TrendingUp, Box, MessageCircle, Bell, LockOpen, HelpCircle, Users, Eye
} from 'lucide-react';

// Import sub-components
import {
    HoverDropdown,
    ToateHoverDropdown,
    MobileCategoryItem,
    MobileCategoriesPanel,
    defaultCategories
} from './navbar/NavbarCategories';
import { UserHoverMenu } from './navbar/NavbarUserMenu';
import { NotificationHoverMenu } from './navbar/NavbarNotifications';
import { MessagesHoverMenu } from './navbar/NavbarMessages';
import ContactAdminModal from '@/components/contact/ContactAdminModal';
import UsefulLinksModal from '@/components/layout/UsefulLinksModal';

export const Navbar = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications, removeNotification, isConnected } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if we're on admin page
    const isAdminPage = location.pathname.startsWith('/admin');
    
    // Check if navbar should be sticky on mobile for specific pages
    const shouldBeStickyOnMobile = () => {
        const path = location.pathname;
        // Frontpage, Toate vânzările, Categorii, Subcategorii, Listing view, User view, Setări cont, Notificări
        if (path === '/' || path === '/home') return true; // Frontpage
        if (path === '/listings' || path.startsWith('/listings')) return true; // Toate vânzările
        if (path === '/all-listings') return true; // All listings
        if (path === '/all-sales') return true; // Preț fix
        if (path === '/all-auctions') return true; // Licitații
        if (path.startsWith('/category/')) return true; // Categorii și Subcategorii
        if (path.startsWith('/listing/')) return true; // Listing view
        if (path.startsWith('/user/') || path.startsWith('/profile/')) return true; // User view
        if (path === '/settings' || path.startsWith('/settings')) return true; // Setări cont
        if (path === '/dashboard' || path.startsWith('/dashboard')) return true; // Dashboard (includes settings tab)
        if (path === '/notifications') return true; // Notificări
        return false;
    };
    
    const isStickyMobile = shouldBeStickyOnMobile();
    
    // Desktop search expanded state
    const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
    const desktopSearchInputRef = useRef(null);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    const [showRecentSearches, setShowRecentSearches] = useState(false);
    const searchInputRef = useRef(null);
    const searchContainerRef = useRef(null);
    
    // Mobile state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
    const [contactAdminOpen, setContactAdminOpen] = useState(false);
    const [usefulLinksOpen, setUsefulLinksOpen] = useState(false);
    
    // Data state
    const [categories, setCategories] = useState(defaultCategories);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [inviteCode, setInviteCode] = useState('');
    const [dashboardCounts, setDashboardCounts] = useState({
        participating: 0, buying: 0, selling: 0, favorites: 0, forSale: 0, sold: 0, following: 0
    });

    // Close mobile menus when resizing to desktop
    useEffect(() => {
        const handleResize = () => {
            // lg breakpoint is 1024px in Tailwind
            if (window.innerWidth >= 1024) {
                setMobileMenuOpen(false);
                setMobileSearchOpen(false);
                setMobileCategoriesOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        // Also check on mount
        handleResize();
        
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load categories from API
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const res = await categoriesAPI.getAll();
                if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                    setCategories(res.data);
                }
            } catch (error) {
                console.error('Error loading categories:', error);
            }
        };
        loadCategories();
    }, []);

    // Load recent searches from localStorage
    useEffect(() => {
        try {
            const saved = localStorage.getItem('colectarium_recent_searches');
            if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
        } catch (e) { console.error('Error loading recent searches:', e); }
    }, []);

    // Fetch unread messages - refetch on route change and periodically
    useEffect(() => {
        if (isAuthenticated) {
            const fetchUnread = async () => {
                try {
                    const res = await messagesAPI.getUnreadCount();
                    setUnreadMessages(res.data.unread_count || 0);
                } catch (error) { console.error('Error fetching unread count:', error); }
            };
            fetchUnread();
            // Poll every 3 seconds for faster updates when entering conversations
            const interval = setInterval(fetchUnread, 3000);
            return () => clearInterval(interval);
        } else {
            setUnreadMessages(0);
        }
    }, [isAuthenticated, location.pathname]); // Refetch on route change

    // Update unread messages when new message notification arrives
    useEffect(() => {
        if (isAuthenticated && notifications.length > 0) {
            const latestNotification = notifications[0];
            if (latestNotification?.type === 'message' && !latestNotification.read) {
                // Refetch unread count when a new message notification arrives
                const fetchUnread = async () => {
                    try {
                        const res = await messagesAPI.getUnreadCount();
                        setUnreadMessages(res.data.unread_count);
                    } catch (error) { console.error('Error fetching unread count:', error); }
                };
                fetchUnread();
            }
        }
    }, [isAuthenticated, notifications]);

    /* DISABLED: Invite functionality temporarily disabled
    // Fetch invite code
    useEffect(() => {
        if (isAuthenticated) {
            const fetchInviteCode = async () => {
                try {
                    const res = await userAPI.getInviteCode();
                    setInviteCode(res.data?.invite_code || '');
                } catch (error) { console.error('Error fetching invite code:', error); }
            };
            fetchInviteCode();
        }
    }, [isAuthenticated]);
    */

    // Fetch dashboard counts for mobile menu
    useEffect(() => {
        if (isAuthenticated && mobileMenuOpen) {
            const fetchCounts = async () => {
                try {
                    const [bidsRes, buyOrdersRes, sellOrdersRes, likesRes, myListingsRes, followingRes] = await Promise.all([
                        listingsAPI.getMyBids().catch(() => ({ data: [] })),
                        ordersAPI.getAll('buyer').catch(() => ({ data: [] })),
                        ordersAPI.getAll('seller').catch(() => ({ data: [] })),
                        listingsAPI.getMyLikes().catch(() => ({ data: [] })),
                        listingsAPI.getMy().catch(() => ({ data: [] })),
                        usersAPI.getFollowedSellers().catch(() => ({ data: [] }))
                    ]);
                    const myListings = Array.isArray(myListingsRes.data) ? myListingsRes.data : [];
                    const sellingOrders = Array.isArray(sellOrdersRes.data) ? sellOrdersRes.data : [];
                    const forSale = myListings.filter(l => l.status === 'active').length;
                    
                    // Calculate sold count: only non-finalized sales (exclude completed, cancelled)
                    const finalizedStatuses = ['completed', 'cancelled'];
                    const nonFinalizedSoldCount = myListings.filter(l => {
                        if (l.status !== 'sold') return false;
                        const order = sellingOrders.find(o => o.listing_id === l.listing_id);
                        if (!order) return true;
                        return !finalizedStatuses.includes(order.order_status);
                    }).length;
                    
                    setDashboardCounts({
                        participating: Array.isArray(bidsRes.data) ? bidsRes.data.length : 0,
                        buying: Array.isArray(buyOrdersRes.data) ? buyOrdersRes.data.length : 0,
                        selling: Array.isArray(sellOrdersRes.data) ? sellOrdersRes.data.length : 0,
                        favorites: Array.isArray(likesRes.data) ? likesRes.data.length : 0,
                        forSale: forSale,
                        sold: nonFinalizedSoldCount,
                        following: Array.isArray(followingRes.data) ? followingRes.data.length : 0
                    });
                } catch (error) { console.error('Error fetching dashboard counts:', error); }
            };
            fetchCounts();
        }
    }, [isAuthenticated, mobileMenuOpen]);

    // Dispatch event when avatar menu opens, and listen for other menus opening
    useEffect(() => {
        if (mobileMenuOpen) {
            window.dispatchEvent(new CustomEvent('avatarMenuOpened'));
        }
    }, [mobileMenuOpen]);

    useEffect(() => {
        const handleOtherMenuOpened = () => {
            setMobileMenuOpen(false);
        };
        window.addEventListener('floatingMenuOpened', handleOtherMenuOpened);
        window.addEventListener('adminFloatingMenuOpened', handleOtherMenuOpened);
        return () => {
            window.removeEventListener('floatingMenuOpened', handleOtherMenuOpened);
            window.removeEventListener('adminFloatingMenuOpened', handleOtherMenuOpened);
        };
    }, []);

    // Search suggestions debounce
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (searchQuery.trim().length < 2) {
                setSearchSuggestions([]);
                setShowRecentSearches(searchQuery.trim().length === 0 && recentSearches.length > 0);
                return;
            }
            setShowRecentSearches(false);
            try {
                const res = await listingsAPI.search(searchQuery.trim(), 5);
                const listings = res.data?.listings || res.data || [];
                setSearchSuggestions(Array.isArray(listings) ? listings.slice(0, 5) : []);
                setShowSuggestions(listings.length > 0);
            } catch (error) {
                console.error('Search suggestions error:', error);
                setSearchSuggestions([]);
            }
        };
        const timer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, recentSearches.length]);

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
                setShowRecentSearches(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Helpers
    const saveRecentSearch = (query) => {
        if (!query.trim()) return;
        const updated = [query.trim(), ...recentSearches.filter(s => s.toLowerCase() !== query.trim().toLowerCase())].slice(0, 5);
        setRecentSearches(updated);
        try { localStorage.setItem('colectarium_recent_searches', JSON.stringify(updated)); } catch (e) {}
    };

    const clearRecentSearches = () => {
        setRecentSearches([]);
        try { localStorage.removeItem('colectarium_recent_searches'); } catch (e) {}
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            saveRecentSearch(searchQuery.trim());
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
            setShowSuggestions(false);
            setShowRecentSearches(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        saveRecentSearch(suggestion.title);
        setSearchQuery('');
        setShowSuggestions(false);
        navigate(`/listing/${suggestion.slug || suggestion.listing_id}`);
    };

    const handleRecentSearchClick = (searchTerm) => {
        saveRecentSearch(searchTerm);
        setShowRecentSearches(false);
        navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    };

    const handleLogout = async () => {
        await logout();
        window.location.href = '/';
    };

    const closeMobilePanels = () => {
        setMobileMenuOpen(false);
        setMobileCategoriesOpen(false);
        setMobileSearchOpen(false);
    };

    return (
        <header className="fixed lg:sticky top-0 left-0 right-0 z-50 w-full bg-white border-b border-gray-200 shadow-xs" data-testid="navbar">
            <div className="container mx-auto px-2 sm:px-4 lg:px-8 max-w-full">
                {/* Main navbar */}
                <div className="flex items-center justify-between h-12 lg:h-14 lg:justify-between">
                    {/* Mobile: All elements in one row with equal spacing */}
                    <div className="lg:hidden flex items-center justify-between w-full">
                        {/* Logo */}
                        <button 
                            onClick={() => {
                                // Close mobile menus
                                setMobileMenuOpen(false);
                                setMobileCategoriesOpen(false);
                                setMobileSearchOpen(false);
                                
                                // FORCE complete refresh - clear ALL possible cached states
                                sessionStorage.removeItem('infinite_scroll_home');
                                sessionStorage.removeItem('nav_away_home');
                                sessionStorage.removeItem('locked_scroll_home');
                                sessionStorage.setItem('force_fresh_home', 'true');
                                
                                // Also clear all_listings keys for AllListingsPage
                                sessionStorage.removeItem('infinite_scroll_all_listings');
                                sessionStorage.removeItem('nav_away_all_listings');
                                
                                // Check last view preference and navigate accordingly
                                const lastView = localStorage.getItem('lastListingView');
                                window.scrollTo(0, 0);
                                if (lastView === 'my-domains' && isAuthenticated) {
                                    navigate('/my-domains');
                                } else {
                                    navigate('/all-listings');
                                }
                            }}
                            className="flex items-center gap-1.5 font-heading font-bold text-base text-foreground shrink-0" 
                            data-testid="logo"
                        >
                            <img src="/logo.png" alt="Colectarium" className="w-7 h-7 rounded-lg shrink-0 object-contain" />
                            <span>Colectarium</span>
                        </button>

                        {/* Mobile Action Buttons */}
                        <div className="flex items-center gap-3">
                            {!mobileSearchOpen ? (
                                <Button variant="outline" size="icon" className="rounded-full shrink-0 h-9 w-9"
                                    onClick={() => { setMobileSearchOpen(true); setMobileMenuOpen(false); setMobileCategoriesOpen(false); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                                    data-testid="mobile-search-btn">
                                    <Search className="w-4 h-4" />
                                </Button>
                            ) : (
                                <form onSubmit={handleSearch} className="flex-1 relative max-w-[200px]">
                                    <div className="relative flex items-center">
                                        <div className="relative flex-1">
                                            <Button type="button" variant="ghost" size="sm" className="absolute left-1 top-1/2 -translate-y-1/2 rounded-full p-1.5 h-7 w-7 shrink-0 z-10"
                                                onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); setShowSuggestions(false); setShowRecentSearches(false); }}>
                                                <X className="w-4 h-4" />
                                            </Button>
                                            <Input ref={searchInputRef} type="search" placeholder="Caută..." value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                onFocus={() => { if (searchQuery.length >= 2) setShowSuggestions(true); else if (recentSearches.length > 0) setShowRecentSearches(true); }}
                                                className="pl-10 pr-10 rounded-full bg-gray-50/50 border-0 h-9 w-full" autoComplete="off" />
                                            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        </div>
                                    </div>

                                    {/* Mobile Recent Searches */}
                                    {showRecentSearches && recentSearches.length > 0 && !showSuggestions && (
                                        <div className="absolute top-full right-0 w-[250px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                            <div className="relative">
                                                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50/80">
                                                    <span className="text-sm font-medium text-gray-700">Căutări recente</span>
                                                    <button onClick={() => { setRecentSearches([]); localStorage.removeItem('recentSearches'); }}
                                                        className="text-xs text-primary hover:underline">Șterge</button>
                                                </div>
                                                <div className="max-h-[200px] overflow-y-auto">
                                                    {recentSearches.map((search, index) => (
                                                        <button key={index} onClick={() => { setSearchQuery(search); navigate(`/search?q=${encodeURIComponent(search)}`); setShowRecentSearches(false); setMobileSearchOpen(false); }}
                                                            className="w-full px-4 py-2.5 text-left bg-transparent hover:bg-gray-200 flex items-center gap-2 text-sm">
                                                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                                                            <span className="truncate">{search}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Mobile Search Suggestions */}
                                    {showSuggestions && searchSuggestions.length > 0 && (
                                        <div className="absolute top-full right-0 w-[250px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                            <div className="max-h-[250px] overflow-y-auto">
                                                {searchSuggestions.map((suggestion) => (
                                                    <Link key={suggestion.listing_id} to={`/listing/${suggestion.slug || suggestion.listing_id}`}
                                                        onClick={() => { setShowSuggestions(false); setMobileSearchOpen(false); setSearchQuery(''); }}
                                                        className="flex items-center gap-3 p-2.5 bg-transparent hover:bg-gray-200 border-b last:border-0">
                                                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                                                            {suggestion.images?.[0] ? (
                                                                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/thumbnail/${suggestion.images[0]}?w=80`}
                                                                    alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center"><Package className="w-4 h-4 text-gray-400" /></div>
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium truncate">{suggestion.title}</p>
                                                            <p className="text-xs text-primary font-semibold">{suggestion.price || suggestion.current_price || suggestion.starting_price} RON</p>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </form>
                            )}

                            {isAuthenticated && !mobileSearchOpen && (
                                <>
                                    <div className="relative">
                                        <Button 
                                            variant="outline" 
                                            size="icon" 
                                            className="rounded-full shrink-0 h-9 w-9"
                                            onClick={() => {
                                                if (location.pathname === '/messages') {
                                                    navigate('/');
                                                } else {
                                                    navigate('/messages');
                                                }
                                            }}
                                            data-testid="mobile-messages-btn"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </Button>
                                        {unreadMessages > 0 && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium pointer-events-none">
                                                {unreadMessages > 9 ? '9+' : unreadMessages}
                                            </span>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <Button 
                                            variant="outline" 
                                            size="icon" 
                                            className="rounded-full shrink-0 h-9 w-9"
                                            onClick={() => {
                                                if (location.pathname === '/notifications') {
                                                    navigate('/');
                                                } else {
                                                    navigate('/notifications');
                                                }
                                            }}
                                            data-testid="mobile-notifications-btn"
                                        >
                                            <Bell className="w-4 h-4" />
                                        </Button>
                                        {unreadCount > 0 && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-medium pointer-events-none">
                                                {unreadCount > 9 ? '9+' : unreadCount}
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}

                            {/* Messages and Notifications icons for non-authenticated users on mobile */}
                            {!isAuthenticated && !mobileSearchOpen && (
                                <>
                                    <div className="relative">
                                        <Button 
                                            variant="outline" 
                                            size="icon" 
                                            className="rounded-full shrink-0 h-9 w-9"
                                            onClick={() => navigate('/login')}
                                            data-testid="mobile-guest-messages-btn"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    <div className="relative">
                                        <Button 
                                            variant="outline" 
                                            size="icon" 
                                            className="rounded-full shrink-0 h-9 w-9"
                                            onClick={() => navigate('/login')}
                                            data-testid="mobile-guest-notifications-btn"
                                        >
                                            <Bell className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </>
                            )}

                            {!mobileSearchOpen && (
                                isAuthenticated ? (
                                    <Button variant="ghost" size="icon"
                                        className={`rounded-full shrink-0 h-9 w-9 overflow-hidden border-2 transition-colors ${mobileMenuOpen ? 'border-primary' : 'border-gray-200'}`}
                                        onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileCategoriesOpen(false); setMobileSearchOpen(false); }}
                                        data-testid="mobile-menu-btn">
                                        {mobileMenuOpen ? (
                                            <X className="w-4 h-4" />
                                        ) : user?.picture || user?.profile_picture_url ? (
                                            <img src={user.profile_picture_url || user.picture} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                                                {user?.name?.charAt(0)?.toUpperCase() || user?.username?.charAt(0)?.toUpperCase() || '?'}
                                            </div>
                                        )}
                                    </Button>
                                ) : (
                                    <Button variant={mobileMenuOpen ? "default" : "outline-solid"} size="sm"
                                        className={`rounded-full shrink-0 px-3 ${mobileMenuOpen ? 'bg-primary text-white' : ''}`}
                                        onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMobileCategoriesOpen(false); setMobileSearchOpen(false); }}
                                        data-testid="mobile-menu-btn">
                                        {mobileMenuOpen ? <X className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                                    </Button>
                                )
                            )}
                        </div>
                    </div>

                    {/* Desktop: Logo on left */}
                    <button 
                        onClick={() => {
                            // FORCE complete refresh - clear ALL possible cached states
                            sessionStorage.removeItem('infinite_scroll_home');
                            sessionStorage.removeItem('nav_away_home');
                            sessionStorage.removeItem('locked_scroll_home');
                            sessionStorage.setItem('force_fresh_home', 'true');
                            
                            // Also clear all_listings keys for AllListingsPage
                            sessionStorage.removeItem('infinite_scroll_all_listings');
                            sessionStorage.removeItem('nav_away_all_listings');
                            
                            // Check last view preference and navigate accordingly
                            const lastView = localStorage.getItem('lastListingView');
                            window.scrollTo(0, 0);
                            if (lastView === 'my-domains' && isAuthenticated) {
                                navigate('/my-domains');
                            } else {
                                navigate('/all-listings');
                            }
                        }}
                        className="hidden lg:flex items-center gap-2 font-heading font-bold text-xl text-foreground shrink-0" 
                        data-testid="logo-desktop"
                    >
                        <img src="/logo.png" alt="Colectarium" className="w-8 h-8 rounded-xl object-contain" />
                        <span>Colectarium</span>
                    </button>

                    {!isAdminPage && (
                        <div className="hidden lg:flex items-center gap-0.5 xl:gap-1 ml-4 xl:ml-8">
                            <ToateHoverDropdown />
                            {categories.map((category) => (
                                <HoverDropdown key={category.category_id || category.id} category={category} />
                            ))}
                        </div>
                    )}

                    {/* Desktop Right Actions */}
                    <div className="hidden lg:flex items-center gap-1 xl:gap-2">
                        {isAuthenticated ? (
                            <>
                                {/* Desktop Search Icon Button - before messages */}
                                <div className="relative" ref={searchContainerRef}>
                                    {!desktopSearchOpen ? (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-full"
                                            onClick={() => {
                                                setDesktopSearchOpen(true);
                                                setTimeout(() => desktopSearchInputRef.current?.focus(), 100);
                                            }}
                                        >
                                            <Search className="w-5 h-5" />
                                        </Button>
                                    ) : (
                                        <form onSubmit={(e) => { handleSearch(e); setDesktopSearchOpen(false); }} className="flex items-center">
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                                    onClick={() => {
                                                        setDesktopSearchOpen(false);
                                                        setSearchQuery('');
                                                        setShowSuggestions(false);
                                                        setShowRecentSearches(false);
                                                    }}
                                                    data-testid="desktop-search-close-btn"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <Input
                                                    ref={desktopSearchInputRef}
                                                    type="search"
                                                    placeholder="Caută produse..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onFocus={() => {
                                                        if (searchQuery.length >= 2) setShowSuggestions(true);
                                                        else if (recentSearches.length > 0) setShowRecentSearches(true);
                                                    }}
                                                    className="w-[280px] xl:w-[350px] h-9 pl-9 pr-4 rounded-full border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                                                    autoComplete="off"
                                                />

                                                {/* Recent Searches Dropdown */}
                                                {showRecentSearches && recentSearches.length > 0 && !showSuggestions && (
                                                    <div className="absolute top-full right-0 w-[320px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                                        
                                                        <div className="relative">
                                                            <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50/80">
                                                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                                                                    <Clock className="w-3 h-3" />
                                                                    Căutări recente
                                                                </span>
                                                                <button type="button" onClick={(e) => { e.preventDefault(); clearRecentSearches(); setShowRecentSearches(false); }}
                                                                    className="text-xs text-muted-foreground hover:text-foreground">
                                                                    Șterge tot
                                                                </button>
                                                            </div>
                                                            <div>
                                                                {recentSearches.map((search, idx) => (
                                                                    <button key={idx} type="button" onClick={() => { handleRecentSearchClick(search); setDesktopSearchOpen(false); }}
                                                                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-transparent hover:bg-gray-200 text-left">
                                                                        <Search className="w-4 h-4 text-muted-foreground" />
                                                                        <span className="text-sm">{search}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Search Suggestions Dropdown */}
                                                {showSuggestions && searchSuggestions.length > 0 && (
                                                    <div className="absolute top-full right-0 w-[350px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                                        
                                                        <div className="relative">
                                                            <div className="max-h-96 overflow-y-auto">
                                                                {searchSuggestions.map((suggestion) => (
                                                                    <button key={suggestion.listing_id} type="button" onClick={() => { handleSuggestionClick(suggestion); setDesktopSearchOpen(false); }}
                                                                        className="w-full flex items-center gap-3 p-3 bg-transparent hover:bg-gray-200 text-left">
                                                                        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                                                                            {suggestion.images?.[0] ? (
                                                                                <img src={suggestion.images[0]} alt={suggestion.title} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center">
                                                                                    <Package className="w-5 h-5 text-muted-foreground" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-sm truncate">{suggestion.title}</p>
                                                                            <span className="text-primary font-semibold text-sm">
                                                                                {suggestion.price?.toLocaleString('ro-RO')} RON
                                                                            </span>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div className="border-t p-2 bg-gray-50/80">
                                                                <Button type="submit" variant="ghost" size="sm" className="w-full text-primary">
                                                                    Vezi toate rezultatele pentru "{searchQuery}"
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </form>
                                    )}
                                </div>
                                
                                <MessagesHoverMenu unreadMessages={unreadMessages} />
                                <NotificationHoverMenu
                                    notifications={notifications}
                                    unreadCount={unreadCount}
                                    markAsRead={markAsRead}
                                    markAllAsRead={markAllAsRead}
                                    clearNotifications={clearNotifications}
                                    removeNotification={removeNotification}
                                    isConnected={isConnected}
                                />
                                <UserHoverMenu user={user} onLogout={handleLogout} inviteCode={inviteCode} onContactAdmin={() => setContactAdminOpen(true)} onUsefulLinksClick={() => setUsefulLinksOpen(true)} />
                            </>
                        ) : (
                            <>
                                {/* Desktop Search for non-authenticated users */}
                                <div className="relative" ref={searchContainerRef}>
                                    {!desktopSearchOpen ? (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-full"
                                            onClick={() => {
                                                setDesktopSearchOpen(true);
                                                setTimeout(() => desktopSearchInputRef.current?.focus(), 100);
                                            }}
                                        >
                                            <Search className="w-5 h-5" />
                                        </Button>
                                    ) : (
                                        <form onSubmit={(e) => { handleSearch(e); setDesktopSearchOpen(false); }} className="flex items-center">
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                                    onClick={() => {
                                                        setDesktopSearchOpen(false);
                                                        setSearchQuery('');
                                                        setShowSuggestions(false);
                                                        setShowRecentSearches(false);
                                                    }}
                                                    data-testid="desktop-search-close-btn-guest"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                                <Input
                                                    ref={desktopSearchInputRef}
                                                    type="search"
                                                    placeholder="Caută produse..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    onFocus={() => {
                                                        if (searchQuery.length >= 2) setShowSuggestions(true);
                                                        else if (recentSearches.length > 0) setShowRecentSearches(true);
                                                    }}
                                                    className="w-[280px] xl:w-[350px] h-9 pl-9 pr-4 rounded-full border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                                                    autoComplete="off"
                                                />

                                                {/* Recent Searches Dropdown */}
                                                {showRecentSearches && recentSearches.length > 0 && !showSuggestions && (
                                                    <div className="absolute top-full right-0 w-[320px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                                        
                                                        <div className="relative">
                                                            <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50/80">
                                                                <span className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                                                                    <Clock className="w-3 h-3" />
                                                                    Căutări recente
                                                                </span>
                                                                <button type="button" onClick={(e) => { e.preventDefault(); clearRecentSearches(); setShowRecentSearches(false); }}
                                                                    className="text-xs text-muted-foreground hover:text-foreground">
                                                                    Șterge tot
                                                                </button>
                                                            </div>
                                                            <div>
                                                                {recentSearches.map((search, idx) => (
                                                                    <button key={idx} type="button" onClick={() => { handleRecentSearchClick(search); setDesktopSearchOpen(false); }}
                                                                        className="w-full flex items-center gap-3 px-4 py-2.5 bg-transparent hover:bg-gray-200 text-left">
                                                                        <Search className="w-4 h-4 text-muted-foreground" />
                                                                        <span className="text-sm">{search}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Search Suggestions Dropdown */}
                                                {showSuggestions && searchSuggestions.length > 0 && (
                                                    <div className="absolute top-full right-0 w-[350px] mt-2 rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50 bg-white/95">
                                                        
                                                        <div className="relative">
                                                            <div className="max-h-96 overflow-y-auto">
                                                                {searchSuggestions.map((suggestion) => (
                                                                    <button key={suggestion.listing_id} type="button" onClick={() => { handleSuggestionClick(suggestion); setDesktopSearchOpen(false); }}
                                                                        className="w-full flex items-center gap-3 p-3 bg-transparent hover:bg-gray-200 text-left">
                                                                        <div className="w-12 h-12 rounded-lg bg-gray-100 overflow-hidden shrink-0">
                                                                            {suggestion.images?.[0] ? (
                                                                                <img src={suggestion.images[0]} alt={suggestion.title} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <div className="w-full h-full flex items-center justify-center">
                                                                                    <Package className="w-5 h-5 text-muted-foreground" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="font-medium text-sm truncate">{suggestion.title}</p>
                                                                            <span className="text-primary font-semibold text-sm">
                                                                                {suggestion.price?.toLocaleString('ro-RO')} RON
                                                                            </span>
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div className="border-t p-2 bg-gray-50/80">
                                                                <Button type="submit" variant="ghost" size="sm" className="w-full text-primary">
                                                                    Vezi toate rezultatele pentru "{searchQuery}"
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </form>
                                    )}
                                </div>
                                {/* Messages and Notifications icons for non-authenticated users */}
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="rounded-full"
                                    onClick={() => navigate('/login')}
                                    data-testid="guest-messages-btn"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                </Button>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="rounded-full"
                                    onClick={() => navigate('/login')}
                                    data-testid="guest-notifications-btn"
                                >
                                    <Bell className="w-5 h-5" />
                                </Button>
                                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn" size="sm">
                                    Autentificare
                                </Button>
                                <Button onClick={() => navigate('/register')} className="rounded-full" size="sm">
                                    Înregistrare
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Categories Panel - hidden on admin page */}
            {!isAdminPage && (
                <MobileCategoriesPanel
                    categories={categories}
                    isOpen={mobileCategoriesOpen}
                    onClose={() => setMobileCategoriesOpen(false)}
                    navigate={navigate}
                />
            )}

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="lg:hidden fixed top-14 right-4 w-56 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200 animate-slide-down max-h-[calc(100vh-5rem)] overflow-y-auto z-50">
                    <div className="p-3">
                        {isAuthenticated ? (
                            <>
                                <nav className="space-y-0.5">
                                    <Link to="/dashboard?tab=participating" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Gavel className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Licitații</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.participating || 0}</span>
                                    </Link>
                                    <Link to="/dashboard?tab=buying" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <ShoppingBag className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Cumpărate</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.buying || 0}</span>
                                    </Link>
                                    <Link to="/dashboard?tab=sold" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Package className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Vândute</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.sold || 0}</span>
                                    </Link>
                                    <Link to="/dashboard?tab=for-sale" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Package className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">De vânzare</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.forSale || 0}</span>
                                    </Link>
                                    <Link to="/dashboard?tab=favorites" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Heart className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Apreciate</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.favorites || 0}</span>
                                    </Link>
                                    <Link to="/dashboard?tab=following" onClick={closeMobilePanels}
                                        className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <Users className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Urmăriți</span>
                                        </div>
                                        <span className="bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">{dashboardCounts.following || 0}</span>
                                    </Link>
                                    <div className="my-2 border-t" />
                                    <Link to="/dashboard?tab=settings" onClick={closeMobilePanels}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                        <Settings className="w-4 h-4 text-primary" />
                                        <span className="font-medium text-sm">Setări cont</span>
                                    </Link>
                                    {user?.role !== 'admin' && user?.role !== 'moderator' && (
                                        <button 
                                            onClick={() => { setContactAdminOpen(true); closeMobilePanels(); }}
                                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-left">
                                            <HelpCircle className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Contactează admin</span>
                                        </button>
                                    )}
                                    {/* Hide admin/moderator links on admin pages - AdminFloatingMenu handles navigation there */}
                                    {user?.role === 'admin' && !isAdminPage && (
                                        <Link to="/admin" onClick={closeMobilePanels}
                                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                            <Shield className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Admin Panel</span>
                                        </Link>
                                    )}
                                    {user?.role === 'moderator' && !isAdminPage && (
                                        <Link to="/admin" onClick={closeMobilePanels}
                                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                                            <Shield className="w-4 h-4 text-primary" />
                                            <span className="font-medium text-sm">Moderare</span>
                                        </Link>
                                    )}
                                    <button 
                                        onClick={() => { setUsefulLinksOpen(true); closeMobilePanels(); }}
                                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-left"
                                        data-testid="mobile-useful-links-btn">
                                        <HelpCircle className="w-4 h-4 text-primary" />
                                        <span className="font-medium text-sm">Linkuri utile</span>
                                    </button>
                                </nav>
                                <div className="mt-2 pt-2 border-t">
                                    <Button variant="outline" className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300 text-xs h-8"
                                        onClick={() => { handleLogout(); closeMobilePanels(); }}>
                                        <LogOut className="w-3 h-3 mr-1" />
                                        Deconectare
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div className="space-y-2">
                                <Button className="w-full rounded-full bg-primary hover:bg-primary/90 text-xs h-8"
                                    onClick={() => { navigate('/login'); closeMobilePanels(); }}>
                                    Autentificare
                                </Button>
                                <Button variant="outline" className="w-full rounded-full text-xs h-8"
                                    onClick={() => { navigate('/register'); closeMobilePanels(); }}>
                                    Înregistrare
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Contact Admin Modal */}
            <ContactAdminModal open={contactAdminOpen} onOpenChange={setContactAdminOpen} />
            
            {/* Useful Links Modal (Mobile) */}
            <UsefulLinksModal open={usefulLinksOpen} onOpenChange={setUsefulLinksOpen} />
        </header>
    );
};

export default Navbar;
